import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import { ProtectedRoute } from '@/components/auth';
import { ErrorBoundary } from '@/components/layouts/ErrorBoundary';
import { Toaster } from '@/components/primitives/Toast';
import { DirectionProvider, ThemeProvider } from '@/components/providers';
import { config } from '@/config';
import { routes } from '@/config/routes';
import { useViewChangeEvents } from '@/hooks/useViewChangeEvents';
import { isMobileOrTabletDevice, MOBILE_GATE_ENABLED } from '@/lib/device';
import { useAuthStore } from '@/modules/auth';
import { isSignupPath } from '@/modules/auth/services/signupLanding';
import { useCoreStore, useThemeStore } from '@/modules/core';
import { TourController } from '@/modules/tour';
import { MobileLandingPage } from '@/pages/MobileLandingPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/** Session-wide parts that must survive navigation (`ProtectedRoute` remounts per route). */
function SessionServices() {
  useViewChangeEvents();
  return <TourController />;
}

export function App() {
  return (
    <ErrorBoundary>
      {/* Outermost, so the theme and the mobile gate can follow the route. */}
      <BrowserRouter basename={config.app.basename}>
        <AppShell />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function AppShell() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const { fetchOrganizations } = useCoreStore();
  const { theme } = useThemeStore();
  const { pathname } = useLocation();
  const hasCheckedAuth = useRef(false);
  // The device doesn't change mid-session, so this is read once. With the gate off, every
  // device takes the desktop path.
  const [isMobile] = useState(() => MOBILE_GATE_ENABLED && isMobileOrTabletDevice());
  const onSignup = isSignupPath(pathname);
  // Phones can sign up, but not use the app yet.
  const showMobileLanding = isMobile && !onSignup;
  // These pages are only designed in light.
  const forcedTheme = isMobile || onSignup ? 'light' : undefined;

  useEffect(() => {
    if (!isMobile && !hasCheckedAuth.current) {
      hasCheckedAuth.current = true;
      void checkAuth();
    }
  }, [checkAuth, isMobile]);

  // Fetch organizations after successful authentication
  useEffect(() => {
    if (isAuthenticated) {
      void fetchOrganizations();
    }
  }, [isAuthenticated, fetchOrganizations]);

  return (
    <ThemeProvider forcedTheme={forcedTheme}>
      <DirectionProvider>
        <QueryClientProvider client={queryClient}>
          {showMobileLanding ? (
            <MobileLandingPage />
          ) : (
            <>
              <Routes>
                {routes.map((route) => {
                  if (route.isProtected) {
                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<ProtectedRoute>{route.element}</ProtectedRoute>}
                      />
                    );
                  }
                  return <Route key={route.path} path={route.path} element={route.element} />;
                })}
              </Routes>
              {isAuthenticated && <SessionServices />}
            </>
          )}
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
        </QueryClientProvider>
        <Toaster theme={forcedTheme ?? theme} />
      </DirectionProvider>
    </ThemeProvider>
  );
}
